import { Preferences } from '@capacitor/preferences'

import { api, type EntitlementResponse } from './api'
import { isNative } from './platform'
import { generateUuid } from './uuid'

/**
 * Device-to-device account transfer — the API surface and the two bits of
 * device-local bookkeeping it needs. See docs/data-transfer.md.
 *
 * The old device mints a code; the new device redeems it and is handed a token
 * for the old account, which survives with its id intact (the portal stores
 * that id and has no way to repoint it). The old device's token is revoked, so
 * this is a move, not a copy — which is what keeps the entitlement merge from
 * being a licence dispenser.
 *
 * Two keys live here:
 *
 *   `lingotalk.transfer.issued`  — the code this device handed out, so
 *     backgrounding the app on the walk to the other tablet doesn't lose it.
 *     Only the hash is on the server; this is the one copy of the plaintext.
 *
 *   `lingotalk.transfer.pending` — the code and nonce this device redeemed
 *     with. Kept until the response has been applied, because the redemption
 *     deletes the row whose token we were using: if the response is lost we
 *     have no credential left and no way to prove we were the redeemer except
 *     these two values.
 *
 * The retired device's side lives in `lib/transfer-retirement.ts`, which the
 * 401 interceptor reaches into and so must not import `api`.
 */

const ISSUED_KEY = 'lingotalk.transfer.issued'
const PENDING_KEY = 'lingotalk.transfer.pending'

/** Length of a transfer code, and the group size we render it in. */
export const TRANSFER_CODE_LENGTH = 8
export const TRANSFER_CODE_GROUP = 4

/** Crockford base32 minus the letters it deliberately omits (I, L, O, U). */
const INVALID_CHARS = /[^0-9A-HJKMNPQRSTVWXYZ]/g

export interface IssuedCode {
  code: string
  expires_at: string
}

export interface TransferCodeState {
  state: 'none' | 'pending' | 'redeemed'
  expires_at: string | null
}

/**
 * The redeem/claim response. Carries the entitlement payload verbatim so it can
 * be fed through the same `applyResponse` the facility redemption uses.
 */
export type TransferRedeemResponse = EntitlementResponse & {
  token: string
  patient_id: string
  practice_name: string | null
  /** What the account holds now — the figure the success screen quotes. */
  imported: { exercises: number; runs: number }
  /** What came off this device. Diagnostic; not shown. */
  moved: { exercises: number; runs: number; assignments: number; purchases: number }
}

/** Strip everything that isn't a code character, uppercase, clamp to length. */
export function cleanTransferCode(value: string): string {
  return value.toUpperCase().replace(INVALID_CHARS, '').slice(0, TRANSFER_CODE_LENGTH)
}

/** `ABCD1234` → `ABCD-1234`. Cosmetic; the server normalizes either way. */
export function groupTransferCode(value: string): string {
  return value.replace(new RegExp(`(.{${TRANSFER_CODE_GROUP}})(?=.)`, 'g'), '$1-')
}

// --- Issuing (the old device) ---------------------------------------------

export async function issueTransferCode(): Promise<IssuedCode> {
  const { data } = await api.post<IssuedCode>('/patient/transfer-code')
  await writeJson(ISSUED_KEY, data)
  return data
}

export async function fetchTransferCodeState(): Promise<TransferCodeState> {
  const { data } = await api.get<TransferCodeState>('/patient/transfer-code')
  return data
}

export async function cancelTransferCode(): Promise<void> {
  await api.delete('/patient/transfer-code')
  await clearKey(ISSUED_KEY)
}

/**
 * The code this device issued, if it is still in its validity window. Lets the
 * dialog come back up showing the same code after the app was backgrounded,
 * which is the normal course of events when somebody walks to another room.
 */
export async function readIssuedCode(): Promise<IssuedCode | null> {
  const stored = await readJson<IssuedCode>(ISSUED_KEY)
  if (stored === null || typeof stored.code !== 'string') return null
  if (hasElapsed(stored.expires_at)) {
    await clearKey(ISSUED_KEY)
    return null
  }
  return stored
}

export async function clearIssuedCode(): Promise<void> {
  await clearKey(ISSUED_KEY)
}

// --- Redeeming (the new device) -------------------------------------------

interface PendingTransfer {
  code: string
  nonce: string
  expires_at: string
}

/**
 * Redeem a code. The nonce is minted and persisted *before* the request goes
 * out, because the server deletes this device's patient row as part of
 * answering: if the response never arrives we need to be able to prove we were
 * the redeemer, and the nonce is that proof.
 */
export async function redeemTransferCode(rawCode: string): Promise<TransferRedeemResponse> {
  const code = cleanTransferCode(rawCode)
  const nonce = generateUuid()

  // 10 minutes is the server's TTL; a minute of slack costs nothing and avoids
  // discarding a pending retry that would still have been accepted.
  const expiresAt = new Date(Date.now() + 11 * 60 * 1000).toISOString()
  await writeJson<PendingTransfer>(PENDING_KEY, { code, nonce, expires_at: expiresAt })

  try {
    const { data } = await api.post<TransferRedeemResponse>('/patient/transfer/redeem', {
      code,
      device_nonce: nonce,
    })
    return data
  } catch (e) {
    // A refusal means nothing happened and there is nothing to retry. Only a
    // transport failure leaves the question open, and that is the one case
    // where the pending record has to survive.
    if (isRefusal(e)) await clearKey(PENDING_KEY)
    throw e
  }
}

/**
 * Finish a transfer whose response we never saw. Returns null when there is
 * nothing outstanding — the ordinary case on every launch.
 */
export async function claimPendingTransfer(): Promise<TransferRedeemResponse | null> {
  const pending = await readJson<PendingTransfer>(PENDING_KEY)
  if (pending === null || typeof pending.code !== 'string' || typeof pending.nonce !== 'string') {
    return null
  }

  if (hasElapsed(pending.expires_at)) {
    await clearKey(PENDING_KEY)
    return null
  }

  try {
    const { data } = await api.post<TransferRedeemResponse>('/patient/transfer/claim', {
      code: pending.code,
      device_nonce: pending.nonce,
    })
    return data
  } catch (e) {
    // The code is gone or was never redeemed — either way this device is not
    // mid-transfer and should stop asking.
    if (isRefusal(e)) await clearKey(PENDING_KEY)
    return null
  }
}

export async function clearPendingTransfer(): Promise<void> {
  await clearKey(PENDING_KEY)
}

// --- helpers ---------------------------------------------------------------

/** 4xx means the server answered; anything else is transport trouble. */
function isRefusal(e: unknown): boolean {
  if (e === null || typeof e !== 'object' || !('response' in e)) return false
  const status = (e as { response?: { status?: unknown } }).response?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

function hasElapsed(isoTimestamp: string | undefined): boolean {
  if (typeof isoTimestamp !== 'string') return true
  const at = Date.parse(isoTimestamp)
  return Number.isNaN(at) || at <= Date.now()
}

async function writeJson<T>(key: string, value: T): Promise<void> {
  if (!isNative) return
  try {
    await Preferences.set({ key, value: JSON.stringify(value) })
  } catch {
    // Best-effort. Losing the issued code costs a re-issue; losing the pending
    // record costs a manual re-entry. Neither is worth failing the flow over.
  }
}

async function readJson<T>(key: string): Promise<T | null> {
  if (!isNative) return null
  try {
    const { value } = await Preferences.get({ key })
    if (!value) return null
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

async function clearKey(key: string): Promise<void> {
  if (!isNative) return
  try {
    await Preferences.remove({ key })
  } catch {
    // ignore
  }
}
